import { ActionContextType } from './action-context-type';
import { ActionDeviceVisibility } from './action-device-visibility';
import { ActionFilterType } from './action-filter-type';
import { ActionPurpose } from './action-purpose';
import { ActionUiZone } from './action-ui-zone';
import View, { ViewController } from '../view';

export type ServiceOperation = Record<string, unknown>;
type ActionWidget = { Id: string; Type?: number; Entity?: string; Url?: string; Controller?: string };

/**
	 * @ngdoc type
	 * @name mx.shell.Action
	 * @module mx.shell
	 *
	 * @description
	 * An implementation of business logic for action
	 * Constructor accepts name, title, icon, important as params like:
	 * ```javascript
	 var action = new mx.shell.Action(name, title, icon);
	 * ```
	 * @property {string} id - Gets or sets id of the action
	 * @property {string} name - Gets or sets internal name of the action
	 * @property {string} title - Gets or sets display name of the action
	 * @property {string} icon - Gets or sets icon of the action
	 * @property {number} position - Gets or sets position of the action in the list. Default: 0
	 * @property {object[]} availableTypes - Gets or sets a collection of types for which the action is used
	 * If empty the action is available for any type. Default: []
	 * @property {mx.shell.ActionContextType} contextType - Gets or sets context type for the action
	 * See {@ref mx.shell.ActionContextType mx.shell.ActionContextType} for available values
	 * Default: {@ref mx.shell.ActionContextType#None mx.shell.ActionContextType.None}
	 * @property {string|function} controller - Gets or sets execution controller action. Can be a name of the controller or controller itself.
	 * <br>This Controller must contain public method with name 'execute', which will be executed when user click on this action button.
	 * @property {string} controllerUrl - Gets or sets link to the execution controller AMD module
	 * @property {mx.shell.ActionUiZone[]} uiZones - Gets or sets an array of {@ref  mx.shell.ActionUiZone  mx.shell.ActionUiZone}
	 * which defines where on UI it should be shown. For example: [mx.shell.ActionUiZone.Dialog, mx.shell.ActionUiZone.Toolbar].
	 * If it is `undefined` (not set) then this property will not make any influence and will be shown everywhere it possible.
	 * @property {mx.shell.ActionDeviceVisibility} deviceVisibility - Gets or sets the rules to show/hide action depends on client device type
	 * @property {int[]} states - An array of object states this action may be applied (e.g. array of SPSCommonClassBase.State).
	 * <br>This array is applied against a specified {@ref mx.shell.ActionFilterType filter}
	 * @property {mx.shell.ActionFilterType} statesFilter - Gets or sets states filtering type for `states`
	 * See {@ref mx.shell.ActionFilterType mx.shell.ActionFilterType} for available values
	 * Default: {@ref mx.shell.ActionFilterType#Any mx.shell.ActionFilterType.Any}
	 * @property {mx.shell.ActionFilterType} typesFilter - Gets or sets types filtering type for `availableTypes`
	 * See {@ref mx.shell.ActionFilterType mx.shell.ActionFilterType} for available values
	 * Default: {@ref mx.shell.ActionFilterType#Any mx.shell.ActionFilterType.Any}
	 * @property htmlClass - Gets or sets css class name that could be used by UI representation of an action
	 * @property {mx.shell.DetailsView} view - A {@ref mx.shell.DetailsView View} this action is related to.
	 * <br>If 'view' is set then this 'view' will be shown when user click on this action button
	 * @property isLazy - Defines will view be changed after init
	 * @property {boolean} reloadPageOnSuccess - a flag to force page reloading on this action execution
	 * @property {number} group - an action group. Actions with the same `group` will be grouped together on UI
	 */
export class MxAction {
	public name: string;
	public title: string;
	public customConfirmationTitle?: string;
	public icon: string;
	public enabled = true;
	public availableTypes: string[] = [];
	public contextType: ActionContextType = ActionContextType.None;
	public uiZones: ActionUiZone[] = [];
	public deviceVisibility: ActionDeviceVisibility = ActionDeviceVisibility.All;
	public purpose: ActionPurpose | null = null;
	public states: number[] = [];
	public statesFilter: ActionFilterType = ActionFilterType.Any;
	public typesFilter: ActionFilterType = ActionFilterType.Any;
	public isLazy = false;
	public isCustomization = false;

	public id?: string;
	public position?: number;
	public controller?: ViewController | string;
	public controllerUrl?: string;
	public controllerParams?: string;
	public view?: View | null;
	public viewFn?: (...params: unknown[]) => Promise<View>;
	public htmlClass?: string;
	public isService = false;
	public scriptFilter?: string;
	public applications?: string[];
	public serviceOperation?: ServiceOperation;
	public Widget?: ActionWidget;
	public reloadPageOnSuccess?: boolean;
	public group?: number;
	public withAnimation = false;
	public description?: string;
	public popover?: string;
	public ariaHidden = false;
	public onPopoverClose: () => void = () => ({});

	constructor(name: string, title: string, icon: string) {
		this.name = name;
		this.title = title;
		this.icon = icon;
	}

	/**
	 * @ngdoc method
	 * @name mx.shell.Action#filter
	 *
	 * @description
	 * Filters a collection of objects to define which objects are available for the actions.
	 * By default all objects are available.
	 * <h3>To be implemented</h3>
	 * @param {mx.shell.Object[]} objects - a collection of {@ref mx.shell.Object objects}.
	 * @returns {mx.shell.Object[]} objects - a collection of filtered {@ref mx.shell.Object objects}.
	 */
	filter(objects: unknown) {
		return objects;
	}
}
